import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import PageExplainer from '@/components/PageExplainer';
import { SponsorBanner } from '@/components/SponsorBanner';
import RichText from '@/components/RichText';
import PageSideRail from '@/components/PageSideRail';
import QuickActions from '@/components/QuickActions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, Building2, MapPin, Users, ExternalLink, Globe, Leaf,
  ChevronLeft, ChevronRight, X, Filter, Award, Star, ArrowRight
} from 'lucide-react';

const BACKEND_URL = '';

function getFaviconUrl(website) {
  if (!website) return null;
  try {
    const domain = new URL(website.startsWith('http') ? website : `https://${website}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  } catch {
    return null;
  }
}

function PartnerCard({ partner, onClick }) {
  const typeColor = { Sponsor: 'bg-brand-navy text-white', Exhibitor: 'bg-brand-sage text-white', Partner: 'bg-slate-600 text-white' };
  // Build multi-pill list from inherited flags (falls back to partner_type)
  const pills = [];
  if (partner.is_sponsor) pills.push('Sponsor');
  if (partner.is_partner) pills.push('Partner');
  if (partner.is_exhibitor) pills.push('Exhibitor');
  if (!pills.length && partner.partner_type) pills.push(partner.partner_type);
  return (
    <Card className="group p-0 overflow-hidden border border-slate-100 hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300 cursor-pointer h-full flex flex-col"
      onClick={() => onClick(partner.sponsor_id)} data-testid={`partner-card-${partner.sponsor_id}`}>
      {/* Header band */}
      <div className="h-2 bg-gradient-to-r from-brand-navy to-brand-sage" />
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {partner.logo_url ? (
              <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }} />
            ) : getFaviconUrl(partner.website) ? (
              <img src={getFaviconUrl(partner.website)} alt={partner.name} className="w-9 h-9 object-contain" onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }} />
            ) : null}
            <div className="w-full h-full items-center justify-center" style={(partner.logo_url || getFaviconUrl(partner.website)) ? { display: 'none' } : { display: 'flex' }}>
              <Building2 className="h-7 w-7 text-brand-navy/50" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-800 group-hover:text-brand-navy transition-colors truncate" title={partner.name}>{partner.name}</h3>
            <div className="flex items-start content-start gap-1.5 flex-wrap mt-1 mb-1 h-[46px] overflow-hidden" data-testid={`partner-pills-${partner.sponsor_id}`}>
              {pills.map(p => (
                <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${typeColor[p] || typeColor.Partner}`} data-testid={`partner-pill-${p.toLowerCase()}-${partner.sponsor_id}`}>
                  {p}
                </span>
              ))}
            </div>
            {partner.tagline && <p className="text-xs text-brand-sage font-medium italic truncate">{partner.tagline}</p>}
          </div>
        </div>

        <div className="text-sm text-slate-600 mb-4 min-h-[2.75rem]"><RichText html={partner.description} className="card card-2" /></div>

        <div className="flex flex-col gap-1.5 text-xs text-slate-500 mb-3">
          {[
            [Building2, partner.sector, 'sector'],
            [MapPin, [partner.hq_location, partner.hq_country].filter(Boolean).join(', '), 'location'],
            [Users, partner.company_size && `${partner.company_size} employees`, 'size'],
          ].map(([Icon, text, key]) => (
            <span key={key} className="flex items-center gap-1 h-4 truncate" data-testid={`partner-${key}-${partner.sponsor_id}`}>
              {text && <><Icon className="h-3 w-3 flex-shrink-0" /><span className="truncate">{text}</span></>}
            </span>
          ))}
        </div>

        <div className="flex flex-wrap content-start gap-1.5 h-[46px] overflow-hidden" data-testid={`partner-focus-${partner.sponsor_id}`}>
          {(partner.sustainability_focus || []).slice(0, 3).map(f => (
            <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{f}</span>
          ))}
          {partner.sustainability_focus?.length > 3 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">+{partner.sustainability_focus.length - 3}</span>
          )}
        </div>

        <div className="mt-auto pt-4 flex justify-end">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-navy" data-testid={`partner-read-more-${partner.sponsor_id}`}>
            View profile
            <span className="h-8 w-8 rounded-full bg-brand-navy text-white flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
              <ArrowRight className="h-4 w-4" />
            </span>
          </span>
        </div>
      </div>
    </Card>
  );
}

export default function PartnerDirectoryPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sector, setSector] = useState(searchParams.get('sector') || '');
  const [size, setSize] = useState(searchParams.get('size') || '');
  const [focus, setFocus] = useState(searchParams.get('focus') || '');
  const [partnerType, setPartnerType] = useState(searchParams.get('type') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [recommendedPartners, setRecommendedPartners] = useState([]);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const fetchOpts = { credentials: 'include', headers };

  const loadPartners = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 12 });
      if (search) params.set('search', search);
      if (sector) params.set('sector', sector);
      if (size) params.set('size', size);
      if (focus) params.set('focus', focus);
      if (partnerType) params.set('partner_type', partnerType);

      const res = await fetch(`${BACKEND_URL}/api/partners?${params}`, fetchOpts);
      if (res.ok) {
        const d = await res.json();
        setPartners(d.partners || []);
        setTotal(d.total || 0);
        setPage(d.page || 1);
        setTotalPages(d.pages || 1);
        if (d.filters) setFilters(d.filters);
      }
    } catch { }
    setLoading(false);
  }, [search, sector, size, focus, partnerType]);

  useEffect(() => { loadPartners(1); }, [search, sector, size, focus, partnerType]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/partners/recommended`, fetchOpts)
      .then(r => r.ok ? r.json() : [])
      .then(data => setRecommendedPartners(data))
      .catch(() => {});
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    const p = new URLSearchParams(searchParams);
    if (val) p.set('search', val); else p.delete('search');
    setSearchParams(p);
  };

  const setFilter = (key, val, setter) => {
    setter(val);
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const clearAllFilters = () => {
    setSearch(''); setSector(''); setSize(''); setFocus(''); setPartnerType('');
    setSearchParams({});
  };

  const activeFilterCount = [sector, size, focus, partnerType].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="partners" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold font-heading gradient-text flex items-center gap-2" data-testid="partner-directory-title">Partner Directory <PageExplainer pageKey="partners" label="About Partners" /></h1>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search partners by name, sector..."
                value={search} onChange={e => handleSearch(e.target.value)}
                className="pl-10" data-testid="partner-search-input" />
            </div>
            <Button variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn" className={showFilters ? 'bg-brand-navy text-white' : ''}>
              <Filter className="h-4 w-4 mr-1.5" />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <Card className="p-5 mb-6 animate-in slide-in-from-top-2" data-testid="filter-panel">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Type</label>
                  <select value={partnerType} onChange={e => setFilter('type', e.target.value, setPartnerType)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="filter-type">
                    <option value="">All Types</option>
                    {(filters.partner_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Sector</label>
                  <select value={sector} onChange={e => setFilter('sector', e.target.value, setSector)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="filter-sector">
                    <option value="">All Sectors</option>
                    {(filters.sectors || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Sustainability Focus</label>
                  <select value={focus} onChange={e => setFilter('focus', e.target.value, setFocus)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="filter-focus">
                    <option value="">All Focus Areas</option>
                    {(filters.sustainability_focus || []).map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" className="mt-3 text-xs text-slate-500" onClick={clearAllFilters}>
                  <X className="h-3 w-3 mr-1" /> Clear all filters
                </Button>
              )}
            </Card>
          )}

          {/* Results count */}
          <p className="text-sm text-slate-500 mb-4" data-testid="results-count">{total} partner{total !== 1 ? 's' : ''} found</p>

          {/* Recommended Partners */}
          {recommendedPartners.length > 0 && !search && !sector && !size && !focus && !partnerType && (
            <div className="mb-6" data-testid="recommended-partners-section">
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
                <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> Recommended Partners
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendedPartners.map(partner => (
                  <Card key={partner.sponsor_id}
                    className="p-4 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white hover:shadow-md transition-all cursor-pointer ring-1 ring-amber-200/50"
                    data-testid={`recommended-partner-${partner.sponsor_id}`}
                    onClick={() => navigate(`/partners/${partner.sponsor_id}`)}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white border border-amber-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {partner.logo_url ? (
                          <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain p-1.5" onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }} />
                        ) : getFaviconUrl(partner.website) ? (
                          <img src={getFaviconUrl(partner.website)} alt={partner.name} className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextSibling; if (fb) fb.style.display = 'flex'; }} />
                        ) : null}
                        <div className="w-full h-full items-center justify-center" style={(partner.logo_url || getFaviconUrl(partner.website)) ? { display: 'none' } : { display: 'flex' }}>
                          <Building2 className="h-5 w-5 text-amber-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-sm font-bold text-slate-800 truncate">{partner.name}</h4>
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                        </div>
                        {partner.recommended_topic && (
                          <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{partner.recommended_topic}</span>
                        )}
                        {partner.recommended_reason && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{partner.recommended_reason}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Partner Grid */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
            </div>
          ) : partners.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No partners match your criteria</p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-3" onClick={clearAllFilters}>Clear Filters</Button>
              )}
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-grid">
              {partners.map(p => (
                <PartnerCard key={p.sponsor_id} partner={p} onClick={(id) => navigate(`/partners/${id}`)} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => loadPartners(page - 1)} data-testid="prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                onClick={() => loadPartners(page + 1)} data-testid="next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          <QuickActions user={user} exclude={['/partners']} />
            </div>
            <PageSideRail placement="partners" />
          </div>
        </div>
      </main>
    </div>
  );
}
